import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Camera, CheckCircle2, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const field = "w-full rounded-2xl border border-slate-200 bg-surface-deep/60 px-3.5 py-2.5 text-sm text-white placeholder:text-slate-400 outline-none focus:border-gold-400";

const Result = ({ r }) => {
  const pct = r.score / r.out_of;
  return (
    <div data-testid="mark-result" className="space-y-3 animate-rise">
      <div className="flex items-center gap-4 rounded-2xl border border-gold-400/40 bg-gold-400/10 p-4">
        <p data-testid="mark-score" className={`font-display text-5xl leading-none ${pct >= 0.7 ? "text-emerald-400" : pct >= 0.4 ? "text-gold-300" : "text-rose-600"}`}>{r.score}<span className="text-2xl text-slate-500">/{r.out_of}</span></p>
        <p className="text-sm text-slate-800">{r.verdict}</p>
      </div>
      {r.strengths?.length > 0 && <ul className="space-y-1.5">{r.strengths.map((s, i) => <li key={i} className="flex gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" />{s}</li>)}</ul>}
      {r.improvements?.length > 0 && <ul className="space-y-1.5">{r.improvements.map((s, i) => <li key={i} className="flex gap-2 text-sm text-slate-700"><ArrowUpRight className="w-4 h-4 mt-0.5 shrink-0 text-gold-300" />{s}</li>)}</ul>}
      {r.better_answer && <div className="rounded-2xl border border-slate-200 bg-surface-2 p-3"><p className="eyebrow">Full-mark answer</p><p className="mt-1 text-sm text-slate-800">{r.better_answer}</p></div>}
    </div>
  );
};

export const MarkForm = ({ question = "", modelAnswer = "", lockQuestion = false }) => {
  const [q, setQ] = useState(question);
  const [ma, setMa] = useState(modelAnswer);
  const [ans, setAns] = useState("");
  const [marks, setMarks] = useState(4);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => { setQ(question); setMa(modelAnswer); setRes(null); setAns(""); setPhoto(null); }, [question, modelAnswer]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setRes(null);
    const fd = new FormData();
    fd.append("question", q); fd.append("model_answer", ma); fd.append("answer", ans); fd.append("marks", String(marks));
    if (photo) fd.append("files", photo);
    try { const { data } = await api.post("/revision/mark", fd); setRes(data); } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} data-testid="ai-marking-container" className="space-y-2.5">
      {lockQuestion ? <p className="rounded-2xl border border-slate-200 bg-surface-2 p-3 text-sm font-medium text-white">{q}</p>
        : <textarea data-testid="mark-question-input" value={q} onChange={(e) => setQ(e.target.value)} rows={2} maxLength={1500} placeholder="The exam question" className={field} />}
      {!lockQuestion && <textarea data-testid="mark-model-input" value={ma} onChange={(e) => setMa(e.target.value)} rows={2} maxLength={2000} placeholder="Model answer or mark scheme (optional)" className={field} />}
      <textarea data-testid="mark-answer-input" value={ans} onChange={(e) => setAns(e.target.value)} rows={4} maxLength={4000} placeholder="Type your answer, or photograph it below" className={field} />
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-1.5 h-10 rounded-full border border-slate-200 px-3.5 text-xs font-semibold text-slate-700 cursor-pointer hover:border-gold-400/60">
          <Camera className="w-4 h-4" />{photo ? photo.name.slice(0, 18) : "Photo of answer"}
          <input data-testid="mark-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => { setPhoto(e.target.files?.[0] ?? null); e.target.value = ""; }} />
        </label>
        <label className="inline-flex items-center gap-2 text-xs text-slate-600">Marks
          <select data-testid="mark-marks-select" value={marks} onChange={(e) => setMarks(Number(e.target.value))} className="h-10 rounded-full border border-slate-200 bg-surface px-3 text-sm text-white">
            {[1, 2, 3, 4, 5, 6, 8, 9, 10, 12].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button data-testid="mark-submit" disabled={busy || q.trim().length < 3 || (!ans.trim() && !photo)} className="ml-auto inline-flex items-center gap-2 h-10 rounded-full bg-gold-400 px-5 text-sm font-bold text-black hover:bg-gold-300 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}{busy ? "Marking…" : "Mark it"}
        </button>
      </div>
      {res && <Result r={res} />}
    </form>
  );
};

export const MarkDialog = ({ open, onOpenChange, question, modelAnswer }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="mark-dialog" className="bg-surface border border-gold-400/30 text-white rounded-3xl max-w-lg max-h-[90dvh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display text-3xl text-white">Mark my answer</DialogTitle>
        <DialogDescription className="text-slate-600">Daybreyk marks it against the model answer and tells you how to improve.</DialogDescription>
      </DialogHeader>
      <MarkForm question={question} modelAnswer={modelAnswer} lockQuestion />
    </DialogContent>
  </Dialog>
);
